import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  DEFAULT_ONLINE_ORDER_ALERT_SETTINGS,
  ONLINE_ORDER_TUNES,
  readOnlineOrderAlertSettings,
  writeOnlineOrderAlertSettings,
  type OnlineOrderAlertSettings,
  type OnlineOrderTuneId,
} from '@src/notifications/onlineOrderAlertSettings';
import { previewOnlineOrderTune } from '@src/notifications/onlineOrderAlertSounds';

export default function OnlineOrderSettings() {
  const [settings, setSettings] = useState<OnlineOrderAlertSettings>(
    DEFAULT_ONLINE_ORDER_ALERT_SETTINGS
  );

  useEffect(() => {
    setSettings(readOnlineOrderAlertSettings());
  }, []);

  const persist = (next: OnlineOrderAlertSettings) => {
    setSettings(next);
    writeOnlineOrderAlertSettings(next);
  };

  const handleTuneChange = (tuneId: OnlineOrderTuneId) => {
    persist({ ...settings, tuneId });
  };

  const handleVolumeChange = (_event: Event, value: number | number[]) => {
    const volume = Array.isArray(value) ? value[0] : value;
    persist({ ...settings, volume: volume / 100 });
  };

  const handlePreview = () => {
    previewOnlineOrderTune(settings.tuneId, settings.volume);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Online Order Alerts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a notification tune and volume for incoming online orders. Tunes 1–10 are quick
        beeps; tunes 11–15 are louder alerts (~1.2–1.5s). The sound plays once per batch — rapid
        back-to-back orders will not stack multiple beeps.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <FormControl fullWidth size="small">
                <InputLabel id="online-order-tune-label">Notification tune</InputLabel>
                <Select
                  labelId="online-order-tune-label"
                  label="Notification tune"
                  value={settings.tuneId}
                  onChange={(e) => handleTuneChange(e.target.value as OnlineOrderTuneId)}
                >
                  {ONLINE_ORDER_TUNES.map((tune) => (
                    <MenuItem key={tune.id} value={tune.id}>
                      {tune.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={<PlayArrowIcon />}
                disabled={settings.volume <= 0}
                onClick={handlePreview}
                sx={{ height: '40px' }}
              >
                Preview tune
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <VolumeUpIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={600}>
                  Volume: {Math.round(settings.volume * 100)}%
                </Typography>
              </Box>
              <Slider
                value={Math.round(settings.volume * 100)}
                onChange={handleVolumeChange}
                min={0}
                max={100}
                step={5}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
